/*eslint max-lines-per-function: ["error", 190]*/
import React, { useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';

import FormRow from '../../../app/components/FormRow';
import DictionarySelector from '../Selects/DictionarySelector';
import { CreatableClearableSelector } from '../Selects/CreatableClearableSelector';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { Option } from '../Selects/Selector';
import { GetDictionaryDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { TooltipKey } from '../../../common/pokCore/tooltip.enum';

interface PaeBulkParameterMapperFormProps {
  show: boolean;
  onClose: () => void;
  handleUpdate: (
    newPurchaseFormDictId?: string,
    newPurchaseFormDictValue?: string,
    newPurchaseFormDictValueEn?: string,
    newCountryDictId?: string,
    newCountryDictValue?: string,
    newCountryDictValueEn?: string,
    newActionGroup?: string,
    newBallDictId?: string,
    newBallDictValue?: string,
    newBallDictValueEn?: string,
    newPathPhaseDictId?: string,
    newPathPhaseDictValue?: string,
    newPathPhaseDictValueEn?: string,
  ) => void;
  countriesDictionaries?: GetDictionaryDto[];
  pathPhaseDictionaries?: GetDictionaryDto[];
  pathPhaseEnabled?: boolean;
  companyId?: string;
  budgetId?: string;
}

export const PaeBulkParameterMapperForm: React.FC<
  PaeBulkParameterMapperFormProps
> = props => {
  const { t, tk } = useTranslation('projectLng');
  const pok = usePokCore();

  const [newPurchaseFormDict, setNewPurchaseFormDict] = useState<{
    id?: string;
    value?: string;
    valueEn?: string;
  }>({});
  const [newCountryDict, setNewCountryDict] = useState<{
    id?: string;
    value?: string;
    valueEn?: string;
  }>({});
  const [newBallDict, setNewBallDict] = useState<{
    id?: string;
    value?: string;
    valueEn?: string;
  }>({});
  const [newActionGroup, setNewActionGroup] = useState<string>();
  const [newPathPhaseDict, setNewPathPhaseDict] = useState<{
    id?: string;
    value?: string;
    valueEn?: string;
  }>({});

  const handleSave = () => {
    props.handleUpdate(
      newPurchaseFormDict.id,
      newPurchaseFormDict.value,
      newPurchaseFormDict.valueEn,
      newCountryDict.id,
      newCountryDict.value,
      newCountryDict.valueEn,
      newActionGroup,
      newBallDict.id,
      newBallDict.value,
      newBallDict.valueEn,
      newPathPhaseDict.id,
      newPathPhaseDict.value,
      newPathPhaseDict.valueEn,
    );
    clearForm();
  };

  const onClose = () => {
    clearForm();
    props.onClose();
  };

  const clearForm = () => {
    setNewPurchaseFormDict({});
    setNewCountryDict({});
    setNewBallDict({});
    setNewActionGroup('');
    setNewPathPhaseDict({});
  };

  return (
    <Modal
      show={props.show}
      onHide={onClose}
      centered
      backdrop="static"
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>{t(tk.estimates.chooseParamToUpdate)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form className="d-grid gap-3">
          <FormRow
            controlId="countryMarket"
            label={t(tk.estimates.positionCountry)}
          >
            <DictionarySelector
              dictionary={'Kraj/rynek'}
              value={newCountryDict.id || ''}
              allowableValues={props.countriesDictionaries || []}
              onChange={option =>
                setNewCountryDict({
                  id: option.value,
                  value: option.labelPl,
                  valueEn: option.labelEn,
                })
              }
            />
          </FormRow>
          {props.pathPhaseEnabled &&
            (props.pathPhaseDictionaries?.length || 0) > 0 && (
              <FormRow
                controlId="pathPhase"
                label={t(tk.estimates.positionPhase)}
              >
                <DictionarySelector
                  dictionary={'Faza'}
                  value={newPathPhaseDict.id || ''}
                  allowableValues={props.pathPhaseDictionaries || []}
                  onChange={option =>
                    setNewPathPhaseDict({
                      id: option.value,
                      value: option.labelPl,
                      valueEn: option.labelEn,
                    })
                  }
                />
              </FormRow>
            )}
          <FormRow
            controlId="ball"
            label={t(tk.estimates.ball)}
            tooltipKey={TooltipKey.ballTooltip}
          >
            <DictionarySelector
              dictionary={'Produkt (Kulka)'}
              value={newBallDict.id || ''}
              onChange={option =>
                setNewBallDict({
                  id: option.value,
                  value: option.labelPl,
                  valueEn: option.labelEn,
                })
              }
            />
          </FormRow>

          <FormRow
            controlId="purchaseForm"
            label={t(tk.estimates.purchaseForm)}
          >
            <DictionarySelector
              dictionary={'Internet - forma zakupu'}
              value={newPurchaseFormDict.id || ''}
              onChange={option =>
                setNewPurchaseFormDict({
                  id: option.value,
                  value: option.labelPl,
                  valueEn: option.labelEn,
                })
              }
            />
          </FormRow>
          <FormRow controlId="actionGroup" label={t(tk.estimates.actionGroup)}>
            <CreatableClearableSelector
              value={newActionGroup ?? ''}
              valueToShow={true}
              onChange={option =>
                setNewActionGroup(option ? (option as Option).label : option)
              }
              provider={async (text: string) => {
                const options =
                  await pok.estimateItems.getActionGroupsOptionsByTextByCompanyAndBudget(
                    text,
                    props.companyId || '',
                    props.budgetId || '',
                  );
                return [
                  { value: '', label: t(tk.estimates.deleteActionGroup) },
                  ...options,
                ];
              }}
            />
          </FormRow>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>
          {t(tk.button.close)}
        </Button>
        <Button variant="primary" onClick={handleSave}>
          {t(tk.button.save)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
